import React, { useMemo, useState } from 'react';
import { Alert, Modal, Button, Form, Dropdown } from 'react-bootstrap';
import axios from '../../../api/axios';

type ProductOption = {
  _id: string;
  name: string;
  price: number;
};

type OrderDraftItem = {
  productId: string;
  quantity: number;
};

type CreateOrderModalProps = {
  show: boolean;
  setShow: (value: boolean) => void;
  token: string | null;
  products: ProductOption[];
  onSaved: () => Promise<void> | void;
};

type CustomerOption = {
  _id: string;
  name?: string;
  phone: string;
  email?: string | null;
};

const CreateOrderModal = ({ show, setShow, token, products, onSaved }: CreateOrderModalProps) => {
  const [error, setError] = useState('');
  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  const [customerMode, setCustomerMode] = useState<'existing' | 'new'>('existing');
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [customerDropdownSearch, setCustomerDropdownSearch] = useState('');
  const [productDropdownSearch, setProductDropdownSearch] = useState('');
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [productDropdownOpen, setProductDropdownOpen] = useState(false);

  const [orderData, setOrderData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    address: '',
    paymentMethod: 'Cash on Delivery',
    notes: '',
  });
  const [items, setItems] = useState<OrderDraftItem[]>([]);

  const filteredCustomers = useMemo(() => {
    const term = customerDropdownSearch.trim().toLowerCase();
    if (!term) return customers;
    return customers.filter((customer) => {
      const label = `${customer.name || ''} ${customer.phone} ${customer.email || ''}`.toLowerCase();
      return label.includes(term);
    });
  }, [customers, customerDropdownSearch]);

  const filteredProducts = useMemo(() => {
    const term = productDropdownSearch.trim().toLowerCase();
    return products
      .filter((product) => !items.some((item) => item.productId === product._id))
      .filter((product) => {
        if (!term) return true;
        return `${product.name}`.toLowerCase().includes(term);
      });
  }, [products, productDropdownSearch, items]);

  const selectedCustomer = useMemo(
    () => customers.find((customer) => customer._id === selectedCustomerId) || null,
    [customers, selectedCustomerId],
  );

  const totalAmount = useMemo(() => {
    return items.reduce((sum, item) => {
      const matched = products.find((product) => product._id === item.productId);
      if (!matched) return sum;
      return sum + (Number(matched.price) * Number(item.quantity));
    }, 0);
  }, [items, products]);

  const loadCustomers = async () => {
    if (!token) {
      return;
    }
    try {
      const response = await axios.get('/customers', {
        headers: { Authorization: `Bearer ${token}` },
        params: { page: 1, limit: 100, sortBy: 'updatedAt', sortOrder: 'desc' },
      });
      const list = Array.isArray(response.data?.customers) ? response.data.customers : [];
      setCustomers(list);
      if (list.length === 0) {
        setCustomerMode('new');
      }
    } catch {
      try {
        const fallback = await axios.get('/orders/customers', {
          headers: { Authorization: `Bearer ${token}` },
          params: { limit: 100 },
        });
        const list = Array.isArray(fallback.data?.customers) ? fallback.data.customers : [];
        setCustomers(list);
        if (list.length === 0) {
          setCustomerMode('new');
        }
      } catch {
        setCustomers([]);
        setCustomerMode('new');
      }
    }
  };

  const resetState = () => {
    setOrderData({
      name: '',
      email: '',
      phoneNumber: '',
      address: '',
      paymentMethod: 'Cash on Delivery',
      notes: '',
    });
    setItems([]);
    setSelectedCustomerId('');
    setCustomerDropdownSearch('');
    setProductDropdownSearch('');
    setCustomerDropdownOpen(false);
    setProductDropdownOpen(false);
    setError('');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setOrderData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleToggleProduct = (productId: string, checked: boolean) => {
    setItems((prevItems) => {
      if (checked) {
        if (prevItems.some((item) => item.productId === productId)) {
          return prevItems;
        }
        return [...prevItems, { productId, quantity: 1 }];
      }
      return prevItems.filter((item) => item.productId !== productId);
    });
  };

  const handleQuantityChange = (productId: string, quantityValue: string) => {
    const parsed = Math.max(1, Number(quantityValue || 1));
    setItems((prevItems) => prevItems.map((item) => (
      item.productId === productId ? { ...item, quantity: parsed } : item
    )));
  };

  const addProductFromDropdown = (productId: string) => {
    handleToggleProduct(productId, true);
    setProductDropdownSearch('');
    setProductDropdownOpen(false);
  };

  const removeItem = (productId: string) => {
    setItems((prevItems) => prevItems.filter((item) => item.productId !== productId));
  };

  const handleCreateCustomerNow = async () => {
    if (!token) {
      setError('Authentication is required.');
      return;
    }

    if (!orderData.name.trim() || !orderData.phoneNumber.trim()) {
      setError('Customer name and phone are required to create customer.');
      return;
    }

    try {
      const response = await axios.post(
        '/customers',
        {
          name: orderData.name.trim(),
          phone: orderData.phoneNumber.trim(),
          email: orderData.email.trim() || undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const createdCustomer = response.data;
      setSelectedCustomerId(createdCustomer._id);
      setCustomerMode('existing');
      await loadCustomers();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to create customer.');
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError('Authentication is required. Please login again.');
      return;
    }

    if (items.length === 0) {
      setError('Please select at least one product.');
      return;
    }

    if (customerMode === 'existing' && !selectedCustomerId) {
      setError('Please select an existing customer or switch to New Customer mode.');
      return;
    }

    if (!orderData.address.trim()) {
      setError('Delivery address is required.');
      return;
    }

    try {
      await axios.post(
        '/orders',
        {
          customer: customerMode === 'existing' ? selectedCustomerId : undefined,
          name: orderData.name.trim(),
          email: orderData.email.trim() || undefined,
          phoneNumber: orderData.phoneNumber.trim(),
          address: orderData.address.trim(),
          items: items.map((item) => {
            const product = products.find((entry) => entry._id === item.productId);
            return {
              productId: item.productId,
              quantity: item.quantity,
              price: Number(product?.price ?? 0),
            };
          }),
          totalAmount,
          paymentMethod: orderData.paymentMethod,
          notes: orderData.notes.trim() || undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      await onSaved();
      setShow(false);
      resetState();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to create order.');
    }
  };

  return (
    <Modal show={show} onHide={() => setShow(false)} size="lg" onShow={loadCustomers}>
      <Modal.Header closeButton>
        <Modal.Title>Create Order</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <Form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Customer Source</Form.Label>
                <Form.Select value={customerMode} onChange={(event) => setCustomerMode(event.target.value as 'existing' | 'new')}>
                  <option value="existing">Select Existing Customer</option>
                  <option value="new">Create New Customer</option>
                </Form.Select>
              </Form.Group>
            </div>
            <div className="col-md-6"></div>
          </div>

          {customerMode === 'existing' ? (
            <Form.Group className="mb-3">
              <Form.Label>Customer</Form.Label>
              <Dropdown
                show={customerDropdownOpen}
                onToggle={(nextShow) => {
                  setCustomerDropdownOpen(Boolean(nextShow));
                  if (!nextShow) {
                    setCustomerDropdownSearch('');
                  }
                }}
                autoClose="outside"
              >
                <Dropdown.Toggle variant="outline-dark" className="w-100 text-start">
                  {selectedCustomer
                    ? `${selectedCustomer.name || selectedCustomer.phone} (${selectedCustomer.phone}${selectedCustomer.email ? `, ${selectedCustomer.email}` : ''})`
                    : 'Select customer'}
                </Dropdown.Toggle>
                <Dropdown.Menu className="w-100 p-2" style={{ maxHeight: '260px', overflowY: 'auto' }}>
                  <Form.Control
                    size="sm"
                    className="mb-2"
                    placeholder="Search by name/phone/email"
                    value={customerDropdownSearch}
                    onChange={(event) => setCustomerDropdownSearch(event.target.value)}
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                    autoFocus
                  />

                  {filteredCustomers.length === 0 ? (
                    <div className="px-2 py-1 text-muted small">No matching customers found.</div>
                  ) : null}

                  {filteredCustomers.map((customer) => (
                    <Dropdown.Item
                      key={customer._id}
                      onClick={() => {
                        setSelectedCustomerId(customer._id);
                        setOrderData((prev) => ({
                          ...prev,
                          name: customer.name || prev.name,
                          email: customer.email || '',
                          phoneNumber: customer.phone || prev.phoneNumber,
                        }));
                        setCustomerDropdownOpen(false);
                        setCustomerDropdownSearch('');
                      }}
                    >
                      {customer.name || customer.phone} ({customer.phone}{customer.email ? `, ${customer.email}` : ''})
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </Form.Group>
          ) : null}

          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Customer Name</Form.Label>
                <Form.Control name="name" value={orderData.name} onChange={handleChange} required />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Customer Phone</Form.Label>
                <Form.Control name="phoneNumber" value={orderData.phoneNumber} onChange={handleChange} placeholder="+923001234567" required />
              </Form.Group>
            </div>
          </div>

          {customerMode === 'new' ? (
            <div className="mb-3">
              <Button type="button" variant="outline-dark" onClick={handleCreateCustomerNow}>Create Customer Now</Button>
            </div>
          ) : null}

          <Form.Group className="mb-3">
            <Form.Label>Email (optional)</Form.Label>
            <Form.Control type="email" name="email" value={orderData.email} onChange={handleChange} />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Delivery Address</Form.Label>
            <Form.Control as="textarea" rows={2} name="address" value={orderData.address} onChange={handleChange} required />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Products</Form.Label>
            <Dropdown
              show={productDropdownOpen}
              onToggle={(nextShow) => {
                setProductDropdownOpen(Boolean(nextShow));
                if (!nextShow) {
                  setProductDropdownSearch('');
                }
              }}
              autoClose="outside"
            >
              <Dropdown.Toggle variant="outline-dark" className="w-100 text-start mb-2">
                Select product
              </Dropdown.Toggle>
              <Dropdown.Menu className="w-100 p-2" style={{ maxHeight: '260px', overflowY: 'auto' }}>
                <Form.Control
                  size="sm"
                  className="mb-2"
                  placeholder="Search products"
                  value={productDropdownSearch}
                  onChange={(event) => setProductDropdownSearch(event.target.value)}
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => event.stopPropagation()}
                  autoFocus
                />

                {filteredProducts.length === 0 ? (
                  <div className="px-2 py-1 text-muted small">No products available.</div>
                ) : null}

                {filteredProducts.map((product) => (
                  <Dropdown.Item key={product._id} onClick={() => addProductFromDropdown(product._id)}>
                    {product.name} - Rs {Number(product.price).toFixed(2)}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>

            {items.length === 0 ? (
              <small className="text-muted">No products selected yet.</small>
            ) : null}

            {items.map((item) => {
              const product = products.find((entry) => entry._id === item.productId);
              if (!product) return null;
              const selected = items.find((item) => item.productId === product._id);
              return (
                <div key={product._id} className="d-flex align-items-center mb-2 gap-2">
                  <div style={{ minWidth: '260px' }}>{product.name} - Rs {Number(product.price).toFixed(2)}</div>
                  <Form.Control
                    style={{ width: '90px' }}
                    type="number"
                    min="1"
                    value={selected?.quantity ?? 1}
                    onChange={(event) => handleQuantityChange(product._id, event.target.value)}
                  />
                  <Button type="button" variant="outline-danger" size="sm" onClick={() => removeItem(product._id)}>Remove</Button>
                </div>
              );
            })}
          </Form.Group>

          <div className="row">
            <div className="col-md-4">
              <Form.Group className="mb-3">
                <Form.Label>Payment Method</Form.Label>
                <Form.Select name="paymentMethod" value={orderData.paymentMethod} onChange={handleChange}>
                  <option value="Cash on Delivery">Cash on Delivery</option>
                </Form.Select>
              </Form.Group>
            </div>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Notes (optional)</Form.Label>
            <Form.Control as="textarea" rows={2} name="notes" value={orderData.notes} onChange={handleChange} />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Total Amount</Form.Label>
            <Form.Control value={`Rs ${totalAmount.toFixed(2)}`} disabled />
          </Form.Group>

          <div className="d-flex justify-content-end gap-2">
            <Button variant="secondary" type="button" onClick={() => setShow(false)}>Cancel</Button>
            <Button variant="dark" type="submit">Create Order</Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default CreateOrderModal;
